import { ChangeEvent, ReactNode } from "react";
import { IconCamera } from "@tabler/icons-react";
import CustomIcon from "../CustomIcon";
import { getInitials } from "../ProfilePicture";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/Button";
import { useTranslation } from "react-i18next";
import { compressAvatarImage } from "../../domains/user/utils/compressAvatarImage";
import { displayToast } from "../app/AppToast";

export default function CustomAvatarUploader({
  avatarUrl,
  fullname,
  buttonText,
  onChange,
  onClear,
  disableUpload = false,
}: {
  avatarUrl: string;
  /** Backs the initials placeholder shown while there is no picture. */
  fullname: string | null;
  buttonText: string;
  onChange: (dataUrl: string) => void;
  onClear?: () => void;
  disableUpload?: boolean;
}) {
  const { t } = useTranslation();
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.item(0);
    if (file !== undefined && file !== null) {
      void compressAvatarImage(file).then(onChange, () => {
        // The picture could not be decoded. Nothing is picked, and the field is
        // already reset below so the same file can be offered again.
        displayToast(t("Something went wrong. Please try again."), "error");
      });

      // Reset image when switching to default then back to upload the same image
      e.target.value = "";
    }
  }

  return (
    <div className="relative flex flex-col w-full">
      <div className="relative mx-auto w-fit">
        {avatarUrl ? (
          <Avatar
            variant="image"
            src={avatarUrl}
            alt=""
            imageFit="cover"
            size="2xl"
            badge={null}
            className="border-2 border-white shadow-card-subtle"
          />
        ) : (
          <Avatar
            variant="initials"
            initials={getInitials(fullname)}
            size="2xl"
            badge={null}
          />
        )}

        {onClear && (
          <div
            className="absolute top-4 right-4"
            onClick={() => {
              onClear();
            }}
          >
            <CustomIcon iconName="cross_circle_big" increase={1} />
          </div>
        )}
      </div>

      <div style={{ height: 20 }} />

      {disableUpload ? null : (
        <UploadAvatarButton disabled={disableUpload} onChange={handleChange}>
          {buttonText === "" ? t("Upload profile picture") : buttonText}
        </UploadAvatarButton>
      )}
    </div>
  );
}

export function UploadAvatarButton({
  children,
  disabled,
  onChange,
}: {
  children: ReactNode;
  disabled: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div style={{ position: "relative" }}>
      <input
        disabled={disabled}
        onChange={(e) => {
          onChange(e);
        }}
        type="file"
        accept="image/*"
        style={{
          opacity: 0,
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          height: "100%",
        }}
      />
      <Button
        variant="primary"
        size="lg"
        className="w-full [&:disabled_svg]:stroke-secondary-bluegrey-4 [&:disabled_svg]:fill-secondary-bluegrey-4"
        disabled={disabled}
        leftIcon={<IconCamera size={20} />}
      >
        {children}
      </Button>
    </div>
  );
}
